import * as React from "react";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Typography,
  Grid,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  TextField,
  Divider,
  CircularProgress,
  Alert,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TextareaAutosize from "@mui/material/TextareaAutosize";

import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";
import SignatureField from "../../../components/SignatureField";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL ||
  process.env.REACT_APP_API_URL;

const fmt = (d) => (d ? moment(d).format("MM/DD/YYYY") : "—");
const pad2 = (n) => String(n).padStart(2, "0");
const fmtUsDateTime = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "—";
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  const yyyy = d.getFullYear();
  let h = d.getHours();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  const hh = pad2(h);
  const min = pad2(d.getMinutes());
  return `${mm}/${dd}/${yyyy} ${hh}:${min} ${ampm}`;
};

async function fetchJSONSafe(url, options = {}) {
  const res = await fetch(url, {
    headers: { Accept: "application/json", ...(options.headers || {}) },
    ...options,
  });
  let data = {};
  try {
    const text = await res.text();
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {};
  }
  return { ok: res.ok, status: res.status, data };
}

const pickText = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return "";
};

// Parse "Digitally signed by NAME on DATE"
const parseSignedMsg = (msg) => {
  if (!msg) return { name: "", dateText: "" };
  const m = String(msg).match(
    /digitally\s*signed\s*by\s*(.+?)\s*on\s*([0-9]{1,2}[/-][0-9]{1,2}[/-][0-9]{2,4}(?:\s+[0-9]{1,2}:[0-9]{2}\s*(?:AM|PM))?)/i
  );
  return m
    ? { name: (m[1] || "").trim(), dateText: (m[2] || "").replace(/\.+$/, "").trim() }
    : { name: "", dateText: "" };
};

// Build single-line signature: "Digitally signed by NAME on MM/DD/YYYY hh:mm AM." or "__"
const buildSignatureLine = (message, typedName, typedDate) => {
  const { name: nFromMsg, dateText } = parseSignedMsg(message);
  const nm = (nFromMsg || typedName || "").trim();
  const dt = dateText || fmtUsDateTime(typedDate);
  if (nm && dt !== "—") return `Digitally signed by ${nm} on ${dt}.`;
  return "—";
};

const expectedRows = [
  { key: "mentalhealthsymptoms", label: "Mental Health\nSymptoms" },
  { key: "mentalhealthserviceneeds", label: "Mental Health\nService Needs" },
  { key: "drugsalcohol", label: "Use of Drugs/Alcohol" },
  { key: "vocational", label: "Vocational" },
  { key: "educational", label: "Educational" },
  { key: "social", label: "Social" },
  { key: "interpersonal", label: "Interpersonal" },
  { key: "selfcare", label: "Self-Care and independent living capacity" },
  { key: "medical", label: "Medical" },
  { key: "dental", label: "Dental" },
  { key: "financial", label: "Financial" },
  { key: "housing", label: "Housing" },
  { key: "transportation", label: "Transportation" },
  { key: "other", label: "Other" },
];

const staffRoles = [
  { key: "staffMHProfessional", label: "MH Professional" },
  { key: "staffMHPractitioner", label: "MH Practitioner" },
  { key: "staffRehabWorker", label: "Rehab Worker" },
  { key: "staffRN", label: "RN" },
];

const supervisorRoles = [
  { key: "supervisorMHProfessional", label: "MH Professional" },
  { key: "supervisorMHPractitioner", label: "MH Practitioner" },
  { key: "supervisorRehabWorker", label: "Rehab Worker" },
  { key: "supervisorRN", label: "RN" },
];

const ARMHSFunctionalAssessmentNewPreview = ({
  patientFormId: propPatientFormId,
  id,
  data = {},
  onClose,
}) => {
  const patientFormId =
    propPatientFormId || id || data?.patientFormId || data?.id;

  const [form, setForm] = React.useState({});
  const [fa, setFa] = React.useState({});
  const [formMeta, setFormMeta] = React.useState(null); // full form record
  const [clientSigSrc, setClientSigSrc] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const [patientSigLink, setPatientSigLink] = React.useState("");
  const [openClientSignatureModal, setOpenClientSignatureModal] =
    React.useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = React.useState(false);
  const sigCanvasRef = React.useRef(null);

  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const LABEL_COL_WIDTH = 340;

  const openClientModal = () => setOpenClientSignatureModal(true);
  const closeClientModal = () => {
    setOpenClientSignatureModal(false);
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
  };

  const handleClose = () => {
    setShowSuccessAlert(false);

    if (onClose) {
      onClose();
    } else {
      try {
        const backdrop = document.querySelector(".MuiBackdrop-root");
        if (backdrop) {
          backdrop.click();
        } else {
          const closeButton = document.querySelector('[aria-label="close"]');
          if (closeButton) {
            closeButton.click();
          } else {
            window.history.back();
          }
        }
      } catch (err) {
        console.error("Could not close form:", err);
        window.location.reload();
      }
    }
  };

  const handleSaveClientSignature = () => {
    if (!sigCanvasRef.current) return;

    const dataUrl = sigCanvasRef.current.getCanvas().toDataURL("image/png");
    setPatientSigLink(dataUrl);
    setClientSigSrc(dataUrl);

    closeClientModal();
  };

  const handleDeleteClientSignature = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this signature?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
      didOpen: () => {
        const swalEl = document.querySelector(".swal2-container");
        if (swalEl) {
          swalEl.style.zIndex = "9999";
        }
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        setPatientSigLink("");
        setClientSigSrc("");

        try {
          const token = localStorage.getItem("neuro_patientportal_auth_token");
          const client = axios.create({
            baseURL: API_BASE_URL,
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          });

          let payload;
          if (formMeta) {
            payload = {
              ...formMeta,
              Id: formMeta.Id ?? formMeta.id ?? patientFormId,
              patientFormId: patientFormId,
              clientsBase64: "",
              clientsURL: "",
              patientSignatureURL: "",
              clientSignatureURL: "",
              imageURLClientSignature: "",
            };
          } else {
            // fallback minimal
            payload = {
              patientFormId: patientFormId,
              clientsBase64: "",
              clientsURL: "",
            };
          }

          await client.post("/Forms/AddPatientForm", payload);

          if (formMeta) {
            setFormMeta((prev) =>
              prev
                ? {
                    ...prev,
                    clientsBase64: "",
                    clientsURL: "",
                    patientSignatureURL: "",
                    clientSignatureURL: "",
                    imageURLClientSignature: "",
                  }
                : prev
            );
          }
          setForm((prev) =>
            prev
              ? {
                  ...prev,
                  clientsBase64: "",
                  clientsURL: "",
                  patientSignatureURL: "",
                  clientSignatureURL: "",
                  imageURLClientSignature: "",
                }
              : prev
          );
          setFa((prev) =>
            prev
              ? {
                  ...prev,
                  clientsBase64: "",
                  clientsURL: "",
                  patientSignatureURL: "",
                  clientSignatureURL: "",
                  imageURLClientSignature: "",
                }
              : prev
          );

          Swal.fire({
            title: "Deleted!",
            text: "Signature has been removed.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
            didOpen: () => {
              const swalEl = document.querySelector(".swal2-container");
              if (swalEl) {
                swalEl.style.zIndex = "9999";
              }
            },
          });
        } catch (e) {
          console.error(e);
          Swal.fire({
            title: "Error!",
            text: "Failed to remove signature.",
            icon: "error",
            didOpen: () => {
              const swalEl = document.querySelector(".swal2-container");
              if (swalEl) {
                swalEl.style.zIndex = "9999";
              }
            },
          });
        }
      }
    });
  };

  const handleSaveForm = async () => {
    if (!patientFormId) return;
    try {
      const token = localStorage.getItem("neuro_patientportal_auth_token");
      const client = axios.create({
        baseURL: API_BASE_URL,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const isBase64 =
        patientSigLink &&
        typeof patientSigLink === "string" &&
        patientSigLink.startsWith("data:");

      let payload;
      if (formMeta) {
        payload = {
          ...formMeta,
          Id: formMeta.Id ?? formMeta.id ?? patientFormId,
          patientFormId: patientFormId,
          ...(isBase64 && { clientsBase64: patientSigLink }),
          // IMPORTANT: we do NOT blank clientsURL here
        };
      } else {
        // fallback minimal if formMeta missing
        payload = {
          patientFormId: patientFormId,
          ...(isBase64 && { clientsBase64: patientSigLink }),
        };
      }

      await client.post("/Forms/AddPatientForm", payload);

      if (formMeta && isBase64) {
        setFormMeta((prev) =>
          prev ? { ...prev, clientsBase64: patientSigLink } : prev
        );
      }
      if (isBase64) {
        setForm((prev) =>
          prev ? { ...prev, clientsBase64: patientSigLink } : prev
        );
        setFa((prev) =>
          prev ? { ...prev, clientsBase64: patientSigLink } : prev
        );
      }

      setShowSuccessAlert(true);
    } catch (e) {
      console.error(e);
      alert("Failed to save form.");
    }
  };

  React.useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const revokeList = [];

    const getPreviewLink = async (path, token) => {
      try {
        if (!path) return "";
        if (/^(https?:|data:|blob:)/i.test(path)) return path;

        const url = `${API_BASE_URL}/PatientDocument/ViewPatientDocument?path=${encodeURIComponent(
          path
        )}`;
        const res = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return "";

        const ct = (res.headers.get("content-type") || "").toLowerCase();
        if (ct.includes("text/plain")) {
          const link = await res.text();
          return link || "";
        }

        const blob = await res.blob();
        if (blob && blob.size > 0) {
          const objUrl = URL.createObjectURL(blob);
          revokeList.push(objUrl);
          return objUrl;
        }
        return "";
      } catch {
        return "";
      }
    };

    const load = async () => {
      setLoading(true);
      setError("");
      setForm({});
      setFa({});
      setClientSigSrc("");
      setPatientSigLink("");
      setFormMeta(null);

      try {
        const token = localStorage.getItem(
          "neuro_patientportal_auth_token"
        );
        if (!token)
          throw new Error("Authentication token not found. Please log in.");

        const headers = { Authorization: `Bearer ${token}` };

        const formUrl = `${API_BASE_URL}/Forms/GetPatientFormsbyId?Id=${patientFormId}`;
        const faUrl = `${API_BASE_URL}/Forms/getFunctionalassesmentById?id=${patientFormId}`;

        const [formRes, faRes] = await Promise.all([
          axios.get(formUrl, { headers, signal: controller.signal }),
          axios.get(faUrl, { headers, signal: controller.signal }),
        ]);

        if (!active) return;

        const formData = Array.isArray(formRes?.data)
          ? formRes.data[0] || {}
          : formRes?.data || {};

        const faData = Array.isArray(faRes?.data)
          ? faRes.data[0] || {}
          : faRes?.data || {};

        setForm(formData);
        setFa(faData);
        setFormMeta(formData || null);

        // ---- Signature loading logic: Base64 first, then URL/path ----
        const base64Candidate =
          formData?.clientsBase64 ||
          faData?.clientsBase64 ||
          formData?.clientSignatureBase64 ||
          faData?.clientSignatureBase64 ||
          formData?.clientBase64 ||
          faData?.clientBase64;

        if (
          base64Candidate &&
          typeof base64Candidate === "string" &&
          base64Candidate.toLowerCase().includes("base64")
        ) {
          if (active) {
            setClientSigSrc(base64Candidate);
            setPatientSigLink(base64Candidate);
          }
        } else {
          const clientSigRaw =
            formData?.clientsURL ||
            formData?.clientSignatureURL ||
            formData?.patientSignatureURL ||
            faData?.clientsURL ||
            faData?.clientSignatureURL ||
            faData?.patientSignatureURL ||
            "";

          if (clientSigRaw) {
            const sigUrl = await getPreviewLink(clientSigRaw, token);
            if (active && sigUrl) {
              setClientSigSrc(sigUrl);
              setPatientSigLink(sigUrl);
            }
          }
        }
      } catch (err) {
        if (active) setError(err.message || "Failed to load data");
        console.error("Error loading form:", err);
      } finally {
        if (active) setLoading(false);
      }
    };

    if (patientFormId) load();

    return () => {
      active = false;
      controller.abort();
      try {
        revokeList.forEach(
          (u) => u.startsWith("blob:") && URL.revokeObjectURL(u)
        );
      } catch {}
    };
  }, [patientFormId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  // Header + patient info
  const title = "ARMHS Functional Assessment (New)";
  const dos = fa.assessmentDate || form.assessmentDate || null;
  const fileVal = form.accountNo ?? "—";
  const nameVal = form.patientName || "—";
  const dobVal = form.dob || fa.patientDOB || null;
  const clientID = form.patientId || fa.patientID || "—";

  const timeFA = fa.initial
    ? "Initial"
    : fa.discharge
    ? "Discharge"
    : fa.ongoing
    ? "Ongoing"
    : "—";
  const faDate =
    fa.functionalDate || fa.assessmentDate || form.assessmentDate || null;
  const diagnosticDate = fa.diagnosticDate || null;

  // Table mappings
  const statusByKey = {
    mentalhealthsymptoms: fa.symptomsStatus,
    mentalhealthserviceneeds: fa.servicesStatus,
    drugsalcohol: fa.drugsalcoholStatus,
    vocational: fa.vocationalStatus,
    educational: fa.educationalStatus,
    social: fa.socialStatus,
    interpersonal: fa.interpersonalStatus,
    selfcare: fa.selfcareStatus,
    medical: fa.medicalStatus,
    dental: fa.dentalStatus,
    financial: fa.financialStatus,
    housing: fa.housingStatus,
    transportation: fa.transportationStatus,
    other: fa.otherStatus,
  };
  const funcByKey = {
    mentalhealthsymptoms: fa.symptomsFunctioning,
    mentalhealthserviceneeds: fa.servicesFunctioning,
    drugsalcohol: fa.drugsalcoholFunctioning,
    vocational: fa.vocationalFunctioning,
    educational: fa.educationalFunctioning,
    social: fa.socialFunctioning,
    interpersonal: fa.interpersonalFunctioning,
    selfcare: fa.selfcareFunctioning,
    medical: fa.medicalFunctioning,
    dental: fa.dentalFunctioning,
    financial: fa.financialFunctioning,
    housing: fa.housingFunctioning,
    transportation: fa.transportationFunctioning,
    other: fa.otherFunctioning,
  };
  const linkByKey = {
    mentalhealthsymptoms: fa.linkTosymptoms,
    mentalhealthserviceneeds: fa.linkToservices,
    drugsalcohol: fa.linkTodrugsalcohol,
    vocational: fa.linkTovocational,
    educational: fa.linkToeducational,
    social: fa.linkTosocial,
    interpersonal: fa.linkTointerpersonal,
    selfcare: fa.linkToselfcare,
    medical: fa.linkTomedical,
    dental: fa.linkTodental,
    financial: fa.linkTofinancial,
    housing: fa.linkTohousing,
    transportation: fa.linkTotransportation,
    other: fa.linkToother,
  };
  const linkMentalByKey = {
    mentalhealthsymptoms: fa.symptomsLinkToMental,
    mentalhealthserviceneeds: fa.servicesLinkToMental,
    drugsalcohol: fa.drugsAlcoholLinkToMental,
    vocational: fa.vocationalLinkToMental,
    educational: fa.educationalLinkToMental,
    social: fa.socialLinkToMental,
    interpersonal: fa.interpersonalLinkToMental,
    selfcare: fa.selfcareLinkToMental,
    medical: fa.medicalLinkToMental,
    dental: fa.dentalLinkToMental,
    financial: fa.financialLinkToMental,
    housing: fa.housingLinkToMental,
    transportation: fa.transportationLinkToMental,
    other: fa.otherLinkToMental,
  };

  // Plans
  const plans = Array.from({ length: 14 }, (_, i) => fa[`plan${i + 1}`]);
  const islicence = !!fa.islicence;

  // Staff name fields (display)
  const staffNameField = pickText(fa.staffName, form.staffName, "—");
  const supervisorNameField = pickText(
    fa.supervisorSignature,
    form.supervisorSignature,
    form.supervisorSignatureName,
    form.supervisedClinician,
    fa.staffSignature,
    "—"
  );
  const directorNameField = pickText(
    fa.directorSignature,
    form.directorSignature,
    "—"
  );
  const staffRoleKey = fa.version || "";
  const supervisorRoleKey = fa.versionSuper || "";

  // Client signature date
  const clientSigDate =
    form.patientSignatureDATE ||
    fa.patientSignatureDATE ||
    form.clientSignatureDATE ||
    fa.clientSignatureDATE ||
    null;

  // Signature messages
  const staffMsg =
    fa.messageStaffSignature ||
    fa.massagestaffSignature ||
    fa.message ||
    form.message ||
    "";
  const supervisorMsg =
    fa.messageSupervisorSignature ||
    fa.messageSupervisorSign ||
    form.messageSupervisorSignature ||
    form.messageSupervisorSign ||
    form.messagesupervisedClinician ||
    "";
  const directorMsg =
    fa.messagetreatmentdirector || form.messagetreatmentdirector || "";

  // Inline single-line signatures with expanded date fallbacks
  const staffSigLine = buildSignatureLine(
    staffMsg,
    pickText(fa.staffSignature, fa.staffName, ""),
    fa.staffdate ||
      form.staffsignatureDATE ||
      form.providerDate ||
      fa.staffSignatureDATE ||
      null
  );

  const supervisorSigLine = buildSignatureLine(
    supervisorMsg,
    supervisorNameField === "—" ? "" : supervisorNameField,
    fa.supervisordate ||
      form.supervisorSignatureDATE ||
      form.supervisorSignatureDate ||
      form.supervisorSignatureDatee ||
      form.supervisorDate ||
      form.SupervisorDate ||
      form.supervisedClinicianDate ||
      fa.supervisorSignatureDATE ||
      null
  );

  const directorSigLine = buildSignatureLine(
    directorMsg,
    directorNameField === "—" ? "" : directorNameField,
    fa.directordate ||
      form.directorDate ||
      fa.treatmentdirectorDate ||
      null
  );

  return (
    <>
      {showSuccessAlert && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 1299,
            }}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "300px",
              padding: "20px",
              backgroundColor: "#fff",
              boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
              borderRadius: "8px",
              textAlign: "center",
              zIndex: 1300,
            }}
          >
            <Typography variant="h6" style={{ marginBottom: "10px" }}>
              Form Saved
            </Typography>
            <Typography style={{ marginBottom: "20px" }}>
              Form saved successfully
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleClose}
            >
              OK
            </Button>
          </div>
        </>
      )}

      <Box sx={{ width: "100%" }}>
        {/* Responsive Header */}
        <Box
          sx={{
            position: "sticky",
            top: "-16px",
            zIndex: (t) => t.zIndex.appBar,
            bgcolor: "#fff",
            p: { xs: 2, sm: 3, md: 4 },
            pb: 0,
            mb: 3,
          }}
        >
          {/* Mobile/Tablet Layout (stacked) */}
          {isMdDown ? (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                  mb: 1,
                }}
              >
                <strong>DOS:</strong> {fmt(dos)}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: "0.9rem", sm: "1.1rem" },
                  mb: 1,
                  textAlign: "center",
                }}
              >
                {title}
              </Typography>
            </Box>
          ) : (
            /* Desktop Layout (absolute positioning) */
            <Box sx={{ position: "relative" }}>
              <Typography
                variant="subtitle2"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                }}
              >
                <strong>DOS:</strong> {fmt(dos)}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  position: "absolute",
                  top: 0,
                  left: "50%",
                  transform: "translateX(-50%)",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                }}
              >
                {title}
              </Typography>
              <Box sx={{ height: 28 }} />
            </Box>
          )}
          <Divider sx={{ mt: isMdDown ? 0 : 0 }} />
        </Box>

        {/* Patient strip */}
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Grid
            container
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Grid item xs={12} md={4}>
              <Typography
                variant="body2"
                sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
              >
                <strong>File #</strong> {fileVal}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                textAlign: { xs: "left", md: "center" },
                mt: { xs: 1, md: 0 },
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
              >
                <strong>Client Name:</strong> {nameVal}
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                textAlign: { xs: "left", md: "right" },
                mt: { xs: 1, md: 0 },
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
              >
                <strong>DOB:</strong> {fmt(dobVal)}
              </Typography>
            </Grid>
          </Grid>
          <Divider sx={{ mb: 2 }} />
        </Box>

        {/* Six info fields - now in grid with multiline */}
        <Box sx={{ mt: 1, px: { xs: 2, sm: 3, md: 4 }, mb: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
              },
              gap: 2,
            }}
          >
            <TextField
              label="Recipient Name"
              value={nameVal || "—"}
              size="small"
              fullWidth
              multiline
              minRows={1}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Client DOB"
              value={fmt(dobVal)}
              size="small"
              fullWidth
              multiline
              minRows={1}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Client ID#"
              value={clientID || "—"}
              size="small"
              fullWidth
              multiline
              minRows={1}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Time of Functional Assessment"
              value={timeFA || "—"}
              size="small"
              fullWidth
              multiline
              minRows={1}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Functional Assessment Date"
              value={fmt(faDate)}
              size="small"
              fullWidth
              multiline
              minRows={1}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Diagnostic Assessment Date"
              value={fmt(diagnosticDate)}
              size="small"
              fullWidth
              multiline
              minRows={1}
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>

        {/* Field | Status | Functioning | Link To Mental - NO SCROLLBAR */}
        <Box sx={{ mt: 1, px: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ width: "100%", border: "1px solid #ddd" }}>
            <Table
              size="small"
              sx={{
                width: "100%",
                borderCollapse: "collapse",
                tableLayout: "fixed",
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ border: "1px solid #ddd", fontWeight: 700, width: "25%" }}
                  >
                    Field
                  </TableCell>
                  <TableCell
                    sx={{ border: "1px solid #ddd", fontWeight: 700, width: "25%" }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{ border: "1px solid #ddd", fontWeight: 700, width: "25%" }}
                  >
                    Functioning
                  </TableCell>
                  <TableCell
                    sx={{ border: "1px solid #ddd", fontWeight: 700, width: "25%" }}
                  >
                    Link To Mental
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expectedRows.map((r, idx) => {
                  const statusVal = pickText(statusByKey[r.key]) || "—";
                  const funcVal =
                    pickText(funcByKey[r.key], linkByKey[r.key]) || "—";
                  const linkMentalVal =
                    pickText(linkMentalByKey[r.key]) || "—";
                  return (
                    <TableRow key={idx}>
                      <TableCell
                        sx={{
                          border: "1px solid #ddd",
                          verticalAlign: "top",
                          fontWeight: 700,
                          whiteSpace: "pre-line",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        {r.label}
                      </TableCell>
                      <TableCell
                        sx={{
                          border: "1px solid #ddd",
                          verticalAlign: "top",
                          whiteSpace: "pre-wrap",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        {statusVal}
                      </TableCell>
                      <TableCell
                        sx={{
                          border: "1px solid #ddd",
                          verticalAlign: "top",
                          whiteSpace: "pre-wrap",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        {funcVal}
                      </TableCell>
                      <TableCell
                        sx={{
                          border: "1px solid #ddd",
                          verticalAlign: "top",
                          whiteSpace: "pre-wrap",
                          wordWrap: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        {linkMentalVal}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Box>

        {/* Plan checkboxes 1..14 */}
        <Box sx={{ mt: 2, px: { xs: 2, sm: 3, md: 4 } }}>
          <Typography sx={{ m: 0, fontSize: { xs: "0.85rem", sm: "1rem" } }}>
            Check which of the areas are addressed in the Treatment Plan:
          </Typography>
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            {plans.map((checked, i) => (
              <Grid key={i} item xs="auto">
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Checkbox
                    checked={!!checked}
                    disabled
                    size="small"
                    sx={{ p: 0, mr: 1 }}
                  />
                  <Typography
                    sx={{ m: 0, fontSize: { xs: "0.85rem", sm: "1rem" } }}
                  >
                    {i + 1}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Summary */}
        <Box sx={{ mt: 2, px: { xs: 2, sm: 3, md: 4 } }}>
          <Typography sx={{ m: 0, fontSize: { xs: "0.85rem", sm: "1rem" } }}>
            Summarize the client's strengths, resources, and all areas of
            functional impairment:
          </Typography>
          <TextareaAutosize
            minRows={3}
            value={fa.functionalImpairment || ""}
            readOnly
            style={{
              width: "100%",
              borderRadius: "8px",
              border: "2px solid #000",
              padding: "10px",
              resize: "none",
              marginTop: "6px",
              fontSize: isSmDown ? "0.85rem" : "1rem",
            }}
          />
        </Box>

        {/* License holder checkbox */}
        <Box
          sx={{
            mt: 1,
            px: { xs: 2, sm: 3, md: 4 },
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            rowGap: 1,
          }}
        >
          <Checkbox checked={!!fa.islicence} disabled size="small" sx={{ p: 0, mr: 1 }} />
          <Typography sx={{ m: 0, fontSize: { xs: "0.85rem", sm: "1rem" } }}>
            License holder did not contact the client's family and other natural
            supports
          </Typography>
        </Box>

        {/* SIGNATURES */}
        <Box sx={{ mt: 3, px: { xs: 2, sm: 3, md: 4 }, pb: 4 }}>
          {/* Client signature row (image + text) with Edit/Remove buttons */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={12} sm={2}>
              <Typography
                sx={{ fontWeight: 600, fontSize: { xs: "0.85rem", sm: "1rem" } }}
              >
                Client
              </Typography>
            </Grid>

            <Grid item xs={12} sm={10}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                {patientSigLink ? (
                  <>
                    <Box
                      sx={{
                        width: 260,
                        height: 90,
                        border: "2px solid #000",
                        borderRadius: 1.5,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#f5f5f5",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={patientSigLink}
                        alt="Client signature"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                    {clientSigDate && (
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: "0.85rem", sm: "1rem" },
                        }}
                      >
                        Signed on {fmtUsDateTime(clientSigDate)}
                      </Typography>
                    )}
                    <Tooltip title="Edit Signature" arrow>
                      <Button
                        onClick={openClientModal}
                        variant="outlined"
                        size="small"
                        style={{ minWidth: "32px", padding: "4px" }}
                        aria-label="Edit Signature"
                      >
                        <EditIcon />
                      </Button>
                    </Tooltip>

                    <Tooltip title="Remove Signature" arrow>
                      <Button
                        onClick={handleDeleteClientSignature}
                        style={{ minWidth: "32px", padding: "4px" }}
                        aria-label="Remove Signature"
                      >
                        <RemoveCircleOutlineIcon style={{ color: "red" }} />
                      </Button>
                    </Tooltip>
                  </>
                ) : (
                  <Link component="button" onClick={openClientModal}>
                    Open signing window
                  </Link>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Staff Name + roles */}
          <Box sx={{ mb: 1.5 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2.5}>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  }}
                >
                  Staff Name:
                </Typography>
              </Grid>
              <Grid item xs={12} md={9.5}>
                <TextField
                  size="small"
                  value={staffNameField}
                  fullWidth
                  multiline
                  minRows={1}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>

            <Grid
              container
              spacing={1}
              sx={{
                mt: 0.5,
                pl: { xs: 0, md: `${LABEL_COL_WIDTH}px` },
              }}
            >
              {staffRoles.map((r) => (
                <Grid key={r.key} item xs="auto">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      checked={fa.version === r.key || !!fa[r.key]}
                      disabled
                      size="small"
                      sx={{ p: 0, mr: 1 }}
                    />
                    <Typography
                      sx={{
                        m: 0,
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      {r.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Staff Signature (required) */}
          <Grid
            container
            spacing={2}
            alignItems="baseline"
            sx={{ mb: 3 }}
          >
            <Grid item xs={12} md={3}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                }}
              >
                Staff Signature (required):
              </Typography>
            </Grid>
            <Grid item xs={12} md={9}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  wordBreak: "break-word",
                }}
              >
                {staffSigLine}
              </Typography>
            </Grid>
          </Grid>

          {/* Clinical Supervisor name + roles */}
          <Box sx={{ mb: 1.5 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={2.5}>
                <Typography
                  sx={{
                    fontWeight: "bold",
                    fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  }}
                >
                  Clinical Supervisor:
                </Typography>
              </Grid>
              <Grid item xs={12} md={9.5}>
                <TextField
                  size="small"
                  value={supervisorNameField}
                  fullWidth
                  multiline
                  minRows={1}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
            </Grid>

            <Grid
              container
              spacing={1}
              sx={{
                mt: 0.5,
                pl: { xs: 0, md: `${LABEL_COL_WIDTH}px` },
              }}
            >
              {supervisorRoles.map((r) => (
                <Grid key={r.key} item xs="auto">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Checkbox
                      checked={fa.versionSuper === r.key || !!fa[r.key]}
                      disabled
                      size="small"
                      sx={{ p: 0, mr: 1 }}
                    />
                    <Typography
                      sx={{
                        m: 0,
                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                      }}
                    >
                      {r.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Clinical Supervisor Signature */}
          <Grid
            container
            spacing={2}
            alignItems="baseline"
            sx={{ mb: 3 }}
          >
            <Grid item xs={12} md={5}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                }}
              >
                Clinical Supervisor/MH Professional Signature:
              </Typography>
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  wordBreak: "break-word",
                }}
              >
                {supervisorSigLine}
              </Typography>
            </Grid>
          </Grid>

          {/* ARMHS Treatment Director Name */}
          <Grid
            container
            spacing={2}
            alignItems="center"
            sx={{ mb: 1.5 }}
          >
            <Grid item xs={12} md={4}>
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                }}
              >
                ARMHS Treatment Director Name:
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                size="small"
                value={directorNameField}
                fullWidth
                multiline
                minRows={1}
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>

          {/* ARMHS Treatment Director Signature */}
          <Grid
            container
            spacing={2}
            alignItems="baseline"
            sx={{ mb: 3 }}
          >
            <Grid item xs={12} md={4}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                }}
              >
                ARMHS Treatment Director Signature:
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "0.85rem", sm: "1rem" },
                  wordBreak: "break-word",
                }}
              >
                {directorSigLine}
              </Typography>
            </Grid>
          </Grid>

          {/* Save Form Button */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveForm}
            >
              Save Form
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Signature Modal */}
      <Dialog
        open={openClientSignatureModal}
        onClose={closeClientModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Signature</DialogTitle>
        <DialogContent>
          <Box sx={{ border: "1px solid #000", mt: 1 }}>
            <SignatureField
              ref={sigCanvasRef}
               
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            onClick={handleSaveClientSignature}
            sx={{ minWidth: 120, mr: 2 }}
          >
            Save
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (sigCanvasRef.current) {
                sigCanvasRef.current.clear();
              }
            }}
            sx={{ minWidth: 120 }}
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ARMHSFunctionalAssessmentNewPreview;